import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Clock, Eye, ArrowLeft, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sidebar } from '../components/Sidebar';
import { NewsletterBox } from '../components/NewsletterBox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const fetchArticle = async () => {
      try {
        const response = await axios.get(`${API}/articles/${slug}`);
        setArticle(response.data);
      } catch (error) {
        console.error('Error fetching article:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getCategoryUrl = (category) => {
    if (category === 'news') return '/noticias';
    if (category === 'tutorial') return '/tutoriais';
    return '/artigos';
  };

  const sanitizeContent = (rawContent) => {
    if (!rawContent) return [];

    const paragraphs = rawContent
      .split(/\n{2,}/)
      .map((paragraph) => {
        let cleaned = paragraph.trim();

        cleaned = cleaned
          .replace(/(?:[—-]\s*)?(?:Foto|Imagem):\s*[^\n]*/gi, '')
          .replace(/\bDivulgação\b/gi, '')
          .replace(/\bFonte:\s*[^\n]*/gi, '')
          .replace(/\(?(?:Foto|Imagem|Divulgação|Fonte)[:][^)]*\)?/gi, '')
          .replace(/\s{2,}/g, ' ')
          .trim();

        return cleaned;
      })
      .filter(Boolean);

    return paragraphs.filter((paragraph) => {
      const normalized = paragraph.toLowerCase();
      if (
        normalized.includes('para ler a matéria completa') ||
        normalized.includes('para ler a matéria completa na fonte original') ||
        normalized.includes('leia o artigo na fonte original') ||
        normalized.includes('veja também')
      ) {
        return false;
      }
      if (/^(foto:|imagem:|divulgação|fonte:)/i.test(paragraph)) {
        return false;
      }
      if (/https?:\/\//i.test(paragraph) && paragraph.trim().length < 120) {
        return false;
      }
      return true;
    });
  };

  const renderParagraph = (paragraph, index) => {
    const urlRegex = /(https?:\/\/\S+)/gi;
    const parts = paragraph.split(urlRegex);

    return (
      <p key={index} className="mb-6 text-slate-200 leading-relaxed">
        {parts.map((part, partIndex) => {
          if (/^https?:\/\//i.test(part)) {
            return (
              <a
                key={partIndex}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                {part}
              </a>
            );
          }
          return <span key={partIndex}>{part}</span>;
        })}
      </p>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-200 flex items-center justify-center">
        <div className="text-center">Carregando artigo...</div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Artigo não encontrado</h1>
            <Link to="/">
              <Button>Voltar para Home</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white py-12" data-testid="article-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <Link to={getCategoryUrl(article.category)} className="inline-flex items-center gap-2 text-slate-300 hover:text-white">
            <ArrowLeft size={20} />
            Voltar
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <article className="lg:col-span-2">
            <div className="aspect-video overflow-hidden rounded-sm mb-8">
              <img
                src={article.featured_image}
                alt={article.image_alt || article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
              <span className="bg-primary/10 text-primary px-3 py-1 rounded-sm font-medium uppercase tracking-wide">
                {article.category}
              </span>
              <span className="flex items-center gap-1">
                <Clock size={16} />
                {formatDate(article.created_at)}
              </span>
              <span className="flex items-center gap-1">
                <Eye size={16} />
                {article.views} visualizações
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              {article.title}
            </h1>

            <div className="flex items-center gap-3 mb-8 pb-8 border-b border-border">
              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-lg">
                {article.author[0]}
              </div>
              <div>
                <div className="font-semibold">{article.author}</div>
                <div className="text-sm text-slate-400">Autor</div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-800 bg-slate-900/80 p-6 mb-8 text-center text-slate-300" id="adsense-top">
              <p className="text-xs uppercase tracking-[0.28em]">Espaço para anúncio</p>
              <div className="mt-4 h-24 rounded-3xl bg-slate-800 flex items-center justify-center">
                <span>Placeholder AdSense 728x90 / responsivo</span>
              </div>
            </div>

            <div
              className={
                article.content_format === 'html'
                  ? "prose prose-invert prose-lg max-w-none mb-12"
                  : "max-w-none mb-12"
              }
              style={{ lineHeight: '1.8' }}
            >
              {article.content_format === 'html' ? (
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }} />
              ) : (
                sanitizeContent(article.content).map(renderParagraph)
              )}
            </div>

            {article.source_url && (
              <div className="rounded-3xl border border-slate-800 bg-slate-900/80 p-6 mb-8">
                <p className="text-sm text-slate-400 mb-2">Fonte original:</p>
                <a
                  href={article.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  {article.source_name || 'Abrir notícia original'}
                </a>
              </div>
            )}

            <div className="flex items-center gap-4 py-6 border-t border-b border-border mb-12">
              <span className="font-semibold">Compartilhar:</span>
              <Button variant="ghost" size="sm" data-testid="share-twitter">
                <Share2 size={18} className="mr-2" />
                Twitter
              </Button>
              <Button variant="ghost" size="sm" data-testid="share-facebook">
                <Share2 size={18} className="mr-2" />
                Facebook
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="bg-muted text-muted-foreground px-3 py-1 rounded-sm text-sm"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-12">
              <NewsletterBox />
            </div>
          </article>

          <div className="lg:sticky lg:top-24 h-fit space-y-6">
            <div className="rounded-3xl border border-slate-800 bg-slate-900/90 p-6 text-center text-slate-300" id="adsense-sidebar">
              <p className="text-xs uppercase tracking-[0.28em] text-slate-500">Anúncio lateral</p>
              <div className="mt-4 h-40 rounded-3xl bg-slate-800 flex items-center justify-center">
                <span>Placeholder AdSense 300x250</span>
              </div>
            </div>
            <Sidebar />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticlePage;